import React, { useState, useEffect } from 'react';
import { X, ChevronDown, ChevronRight, ExternalLink, Map } from 'lucide-react';
import { usePageVisibility } from '../../hooks/usePageVisibility';

interface PageLink {
  hash: string;
  label: string;
  description: string;
  type: 'main' | 'profile-sub' | 'special' | 'test';
}

const ALL_PAGES: PageLink[] = [
  // === Main pages — labels matched with navbar ===
  { hash: '#dashboard', label: '📊 Home', description: 'Community Dashboard (navbar: Home)', type: 'main' },
  { hash: '#connecties', label: '🤝 Netwerk', description: 'Connecties / Netwerk pagina', type: 'main' },
  { hash: '#agenda', label: '📅 Agenda', description: 'Agenda & Meetings', type: 'main' },
  { hash: '#inbox', label: '📬 Inbox', description: 'Berichten', type: 'main' },
  { hash: '#vibes', label: '📊 Vibes', description: 'VibeScore pagina', type: 'main' },
  { hash: '#social-media', label: '🌐 Social', description: 'Social Media Hub', type: 'main' },
  { hash: '#feeds', label: '📰 Feeds', description: 'Externe feeds (uit navbar verborgen)', type: 'main' },
  { hash: '#mijn-verhaal', label: '📖 Mijn Verhaal', description: 'Profiel slider (profiel menu)', type: 'main' },
  { hash: '#privacy', label: '🔒 Privacy', description: 'Privacy Policy', type: 'main' },

  // === Profile sub pages ===
  { hash: '#profile-hub', label: '👤 Profiel Hub', description: 'Profiel pagina', type: 'profile-sub' },
  { hash: '#networkclub-hub', label: '🏢 Netwerkclub Hub', description: 'Netwerkclub beheer', type: 'profile-sub' },
  { hash: '#mastermind-hub', label: '🧠 Mastermind Hub', description: 'Mastermind community', type: 'profile-sub' },
  { hash: '#cards-editor', label: '🃏 Cards Editor', description: 'Drag & Drop kaarten', type: 'profile-sub' },
  { hash: '#membership', label: '⭐ Lidmaatschap', description: 'Membership pagina', type: 'profile-sub' },
  { hash: '#training', label: '🎓 Training Center', description: 'Training & courses', type: 'profile-sub' },
  { hash: '#en', label: '🇬🇧 English Version', description: 'Engelse versie', type: 'profile-sub' },

  // === Special / Admin pages ===
  { hash: '#onboarding', label: '🚀 Onboarding', description: 'Onboarding Wizard (force mode)', type: 'special' },
  { hash: '#admin-schedule', label: '📋 Admin Schedule', description: 'Admin planning', type: 'special' },
  { hash: '#admin-monitoring', label: '📈 Admin', description: 'Monitoring dashboard', type: 'special' },

  // === Test pages (hidden in funding mode) ===
  { hash: '#gemini-test', label: '🤖 Gemini Test', description: 'Gemini AI test (funding mode)', type: 'test' },
  { hash: '#social-sharing', label: '📤 Social Sharing', description: 'Social sharing demo (funding mode)', type: 'test' },
];

const TYPE_LABELS: Record<string, string> = {
  'main': '📄 Hoofd pagina\'s',
  'profile-sub': '👤 Profiel sub-pagina\'s',
  'special': '⚙️ Speciale / Admin pagina\'s',
  'test': '🧪 Test pagina\'s',
};

const TYPE_ORDER = ['main', 'profile-sub', 'special', 'test'];

interface AdminPageNavigatorProps {
  isAdmin: boolean;
  viewMode: 'admin' | 'member';
}

const AdminPageNavigator: React.FC<AdminPageNavigatorProps> = ({ isAdmin, viewMode }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set(['main', 'profile-sub', 'special']));
  const [currentHash, setCurrentHash] = useState(window.location.hash);
  const { isPageOnline, togglePage, canToggle } = usePageVisibility();

  // Track hash changes to highlight current page
  useEffect(() => {
    const onHashChange = () => setCurrentHash(window.location.hash);
    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  }, []);

  // Only show for admins in admin view mode
  if (!isAdmin || viewMode !== 'admin') return null;

  const toggleSection = (type: string) => {
    setExpandedSections(prev => {
      const next = new Set(prev);
      if (next.has(type)) next.delete(type);
      else next.add(type);
      return next;
    });
  };

  const navigateTo = (page: PageLink) => {
    if (page.hash === '#onboarding') {
      // Force onboarding for admin — uses query param so the guard doesn't redirect away
      const url = new URL(window.location.href);
      url.searchParams.set('forceOnboarding', '1');
      url.hash = '#onboarding';
      window.location.href = url.toString();
      window.location.reload();
      return;
    }
    window.location.hash = page.hash;
    setCurrentHash(page.hash);
  };

  const handleToggle = (e: React.MouseEvent, page: PageLink) => {
    e.stopPropagation(); // Don't navigate when clicking the toggle
    console.log('👆 Admin clicked toggle for:', page.hash);
    togglePage(page.hash);
  };

  // Count online/offline
  const onlineCount = ALL_PAGES.filter(p => isPageOnline(p.hash)).length;
  const offlineCount = ALL_PAGES.length - onlineCount;

  const grouped = TYPE_ORDER.map(type => ({
    type,
    label: TYPE_LABELS[type],
    pages: ALL_PAGES.filter(p => p.type === type),
  }));

  return (
    <>
      {/* Floating trigger button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="fixed bottom-24 right-4 z-[9999] w-12 h-12 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-full shadow-lg hover:shadow-xl hover:scale-105 transition-all flex items-center justify-center"
        title="Admin Page Navigator"
        style={{ WebkitTapHighlightColor: 'transparent' }}
      >
        {isOpen ? <X size={20} /> : <Map size={20} />}
        {/* Badge for offline pages */}
        {offlineCount > 0 && !isOpen && (
          <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
            {offlineCount}
          </span>
        )}
      </button>

      {/* Navigator panel */}
      {isOpen && (
        <div className="fixed bottom-40 right-4 z-[9999] w-96 max-h-[70vh] bg-white rounded-xl shadow-2xl border border-gray-200 overflow-hidden flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 bg-gradient-to-r from-purple-600 to-indigo-600 text-white flex-shrink-0">
            <div className="flex items-center gap-2">
              <Map size={16} />
              <span className="font-semibold text-sm">Admin Page Navigator</span>
            </div>
            <button onClick={() => setIsOpen(false)} className="hover:bg-white/20 rounded p-1 transition-colors">
              <X size={16} />
            </button>
          </div>

          {/* Status bar */}
          <div className="px-4 py-2 bg-purple-50 border-b flex-shrink-0 flex items-center justify-between">
            <span className="text-purple-700 text-xs">
              {ALL_PAGES.length} pagina's • Klik om te navigeren
            </span>
            <div className="flex items-center gap-3 text-xs">
              <span className="flex items-center gap-1 text-green-600">
                <span className="w-2 h-2 bg-green-500 rounded-full inline-block"></span>
                {onlineCount} online
              </span>
              {offlineCount > 0 && (
                <span className="flex items-center gap-1 text-red-500">
                  <span className="w-2 h-2 bg-red-400 rounded-full inline-block"></span>
                  {offlineCount} offline
                </span>
              )}
            </div>
          </div>

          {/* Scrollable list */}
          <div className="overflow-y-auto flex-1">
            {grouped.map(group => {
              const groupOnline = group.pages.filter(p => isPageOnline(p.hash)).length;
              const groupTotal = group.pages.length;
              return (
                <div key={group.type}>
                  <button
                    onClick={() => toggleSection(group.type)}
                    className="w-full flex items-center justify-between px-4 py-2.5 bg-gray-50 hover:bg-gray-100 text-sm font-medium text-gray-700 border-b transition-colors"
                  >
                    <span>
                      {group.label} 
                      <span className="text-gray-400 font-normal ml-1">
                        ({groupOnline}/{groupTotal})
                      </span>
                    </span>
                    {expandedSections.has(group.type) 
                      ? <ChevronDown size={14} /> 
                      : <ChevronRight size={14} />
                    }
                  </button>

                  {expandedSections.has(group.type) && (
                    <div className="divide-y divide-gray-50">
                      {group.pages.map(page => {
                        const isActive = currentHash === page.hash;
                        const online = isPageOnline(page.hash);
                        const toggleable = canToggle(page.hash);
                        return (
                          <div
                            key={page.hash}
                            className={`flex items-center transition-colors ${
                              isActive 
                                ? 'bg-indigo-50 border-l-[3px] border-indigo-500' 
                                : 'border-l-[3px] border-transparent'
                            } ${!online ? 'opacity-60' : ''}`}
                          >
                            {/* Page info — clickable to navigate */}
                            <button
                              onClick={() => navigateTo(page)}
                              className="flex-1 text-left px-4 py-2.5 hover:bg-indigo-50 transition-colors group min-w-0"
                            >
                              <div className="flex items-center gap-2">
                                <span className={`text-sm font-medium truncate ${
                                  !online 
                                    ? 'text-gray-400 line-through' 
                                    : isActive 
                                      ? 'text-indigo-700' 
                                      : 'text-gray-800'
                                }`}>
                                  {page.label}
                                </span>
                                {!online && (
                                  <span className="text-[9px] bg-red-100 text-red-600 px-1.5 py-0.5 rounded-full font-medium flex-shrink-0">
                                    OFFLINE
                                  </span>
                                )}
                                <ExternalLink size={12} className="text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0 ml-auto" />
                              </div>
                              <p className="text-xs text-gray-500 mt-0.5">{page.description}</p>
                              <code className="text-[10px] text-indigo-400 mt-0.5 block font-mono">{page.hash}</code>
                            </button>

                            {/* Toggle switch */}
                            <div className="px-3 flex-shrink-0">
                              {toggleable ? (
                                <button
                                  onClick={(e) => handleToggle(e, page)}
                                  className={`relative w-10 h-5 rounded-full transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-purple-400 ${
                                    online 
                                      ? 'bg-green-500' 
                                      : 'bg-gray-300'
                                  }`}
                                  title={online ? 'Pagina offline halen' : 'Pagina online zetten'}
                                >
                                  <span
                                    className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform duration-200 ${
                                      online ? 'translate-x-5' : 'translate-x-0'
                                    }`}
                                  />
                                </button>
                              ) : (
                                <span className="text-[9px] text-gray-400 italic" title="Deze pagina kan niet uitgeschakeld worden">
                                  altijd aan
                                </span>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Footer */}
          <div className="px-4 py-2 bg-gray-50 border-t text-[10px] text-gray-400 flex-shrink-0">
            Toggle = pagina online/offline voor alle gebruikers • Alleen zichtbaar in Admin View
          </div>
        </div>
      )}
    </>
  );
};

export default AdminPageNavigator;
